/**
 * @file useAdminSession.js
 * @description 관리자 로그인 상태를 조회하고 로그인/로그아웃을 처리하는 훅입니다.
 * @used-in pages/admin/AdminPage.jsx
 */

import { useCallback, useEffect, useState } from 'react';
import * as adminApi from '../services/adminApi';

// [기능] 관리자 세션 상태와 로그인/로그아웃 동작을 제공합니다.
// @returns {{status, isAuthenticated, isSubmitting, error, login, logout, markUnauthorized, clearError}}
export function useAdminSession() {
  // [기술] 'checking' 단계를 따로 두어, 세션 확인이 끝나기 전에 로그인 화면이
  //        잠깐 나타났다 사라지는 깜빡임을 방지합니다.
  const [status, setStatus] = useState('checking');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // [기능] 마운트 시 서버에 현재 로그인 여부를 확인합니다.
  // [기술] StrictMode는 개발 중 이펙트를 두 번 실행합니다. 언마운트된 뒤 상태를 바꾸지 않도록
  //        플래그로 응답 반영을 막습니다.
  useEffect(() => {
    let isActive = true;

    adminApi
      .fetchSession()
      .then((authenticated) => {
        if (isActive) setStatus(authenticated ? 'authenticated' : 'anonymous');
      })
      .catch(() => {
        // [비즈니스] 세션 확인 자체가 실패하면(서버 중단 등) 로그인 화면을 보여 줍니다.
        if (isActive) setStatus('anonymous');
      });

    return () => {
      isActive = false;
    };
  }, []);

  // [기능] 관리자 키로 로그인합니다. 성공하면 true를 반환합니다.
  const login = useCallback(async (key) => {
    setIsSubmitting(true);
    setError('');
    try {
      await adminApi.login(key);
      setStatus('authenticated');
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  // [기능] 로그아웃하고 로그인 화면으로 되돌립니다.
  const logout = useCallback(async () => {
    setIsSubmitting(true);
    try {
      await adminApi.logout();
    } catch {
      // [비즈니스] 서버 호출이 실패해도 화면은 로그아웃 상태로 되돌립니다.
      //            쿠키가 남아 있으면 다음 요청의 401에서 다시 정리됩니다.
    } finally {
      setStatus('anonymous');
      setIsSubmitting(false);
    }
  }, []);

  // [기능] 저장 중 401을 받은 패널이 호출해 세션 만료를 알립니다.
  // [비즈니스] 세션은 일정 시간이 지나면 만료되므로, 작업 도중 만료되면
  //            로그인 화면으로 자연스럽게 되돌아가야 합니다.
  const markUnauthorized = useCallback(() => {
    setStatus('anonymous');
    setError('세션이 만료되었습니다. 다시 로그인해 주세요.');
  }, []);

  const clearError = useCallback(() => setError(''), []);

  return {
    status,
    isAuthenticated: status === 'authenticated',
    isSubmitting,
    error,
    login,
    logout,
    markUnauthorized,
    clearError,
  };
}
